import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { MessagesSquare, Trash2, Loader2, ChevronDown, ChevronRight, Pin } from "lucide-react";

const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";
const lbl = "text-xs uppercase tracking-widest text-bone/60 mb-1.5 block";
const CATS = ["Spiritual", "Psychological", "Mental & Emotional Health", "Cultural & Identity", "Collective & Practical"];

export function ForumManager() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ title: "", category: CATS[0], summary: "", opening_commentary: "", author: "The Apocalypz Council", pinned: false });
  const [expanded, setExpanded] = useState(null);
  const [replies, setReplies] = useState([]);

  const load = () => { setLoading(true); api.get("/forum/topics").then((r) => setTopics(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/forum/topics", f);
      toast.success("Topic added");
      setF({ title: "", category: f.category, summary: "", opening_commentary: "", author: "The Apocalypz Council", pinned: false });
      load();
    } catch { toast.error("Failed to add topic"); } finally { setBusy(false); }
  };

  const delTopic = async (t) => {
    if (!window.confirm(`Delete the topic "${t.title}" and all ${t.reply_count} of its replies? This cannot be undone.`)) return;
    try { await api.delete(`/forum/topics/${t.id}`); toast.success("Topic deleted"); if (expanded === t.id) setExpanded(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleReplies = async (t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id); setReplies([]);
    try { const { data } = await api.get(`/forum/topics/${t.id}`); setReplies(data.replies || []); }
    catch { toast.error("Could not load replies"); }
  };

  const delReply = async (rid) => {
    if (!window.confirm("Remove this reply?")) return;
    try { await api.delete(`/forum/replies/${rid}`); toast.success("Reply removed"); setReplies((p) => p.filter((r) => r.id !== rid)); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-forum">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><MessagesSquare size={22} className="text-gold" /> Community Forum</h2>
      <p className="text-bone/50 text-sm mb-6">Add new discussion topics (with opening commentary), delete topics, and remove any reply.</p>

      <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl mb-10" data-testid="admin-forum-form">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Title</label><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="admin-forum-title" /></div>
          <div><label className={lbl}>Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={field} data-testid="admin-forum-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div><label className={lbl}>Summary (short teaser)</label><input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className={field} data-testid="admin-forum-summary" /></div>
        <div><label className={lbl}>Opening Commentary</label><textarea required value={f.opening_commentary} onChange={(e) => setF({ ...f, opening_commentary: e.target.value })} className={field} rows={6} placeholder="The initial commentary that opens the discussion..." data-testid="admin-forum-commentary" /></div>
        <div className="grid grid-cols-2 gap-4 items-end">
          <div><label className={lbl}>Author voice</label><input value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} className={field} /></div>
          <label className="flex items-center gap-2 text-sm text-bone/70 pb-2 cursor-pointer">
            <input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} data-testid="admin-forum-pinned" /> Pin to top
          </label>
        </div>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold/90 disabled:opacity-50 transition-colors" data-testid="admin-forum-submit">
          {busy && <Loader2 size={16} className="animate-spin" />} Add Topic
        </button>
      </form>

      <h3 className="font-serif text-xl text-bone mb-3">Topics ({topics.length})</h3>
      {loading ? (
        <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {topics.map((t) => (
            <div key={t.id} className="bg-surface" data-testid="admin-forum-row">
              <div className="flex items-center gap-3 p-3 text-sm">
                <button onClick={() => toggleReplies(t)} className="text-bone/50 hover:text-gold" data-testid="admin-forum-expand">
                  {expanded === t.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-bone truncate flex items-center gap-2">{t.pinned && <Pin size={12} className="text-gold" />}{t.title}</p>
                  <p className="text-bone/40 text-xs truncate">{t.category} · {t.reply_count} replies</p>
                </div>
                <button onClick={() => delTopic(t)} className="text-bone/50 hover:text-red-400" data-testid="admin-forum-delete-topic"><Trash2 size={15} /></button>
              </div>
              {expanded === t.id && (
                <div className="px-4 pb-4 pl-11 space-y-2" data-testid="admin-forum-replies">
                  {replies.length === 0 ? (
                    <p className="text-bone/40 text-xs">No replies yet.</p>
                  ) : replies.map((r) => (
                    <div key={r.id} className="flex items-start gap-2 bg-black/30 border border-white/5 p-3 text-xs">
                      <div className="flex-1 min-w-0">
                        <p className="text-bone/80"><span className="text-gold">{r.author_name}</span> · {new Date(r.created_at).toLocaleDateString()}</p>
                        <p className="text-bone/60 mt-1 whitespace-pre-wrap">{r.body}</p>
                      </div>
                      <button onClick={() => delReply(r.id)} className="text-bone/40 hover:text-red-400 shrink-0" data-testid="admin-forum-delete-reply"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
